import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { OrgLayout } from "../../components/Shell";
import { ConfirmDialog, PageHeader, useToast } from "../../components/ui";
import { EmptyListMark, EmptyState, ErrorState } from "../../components/States";
import { supportAccessApi, staffKeys } from "../../features/staff/api";
import { formatAccessLength, formatDateTime } from "../../lib/format";
import { useAuth } from "../../auth/store";
import type { SupportAccessGrant } from "@backstory/protocol";

type PendingAct = { id: string; op: "accept" | "deny" | "revoke"; who: string; unmask: boolean; length: string };

const STATUS: Record<string, { label: string; tone: string }> = {
  pending: { label: "Waiting", tone: "info" },
  active: { label: "Active", tone: "ok" },
  revoked: { label: "Ended", tone: "neutral" },
  expired: { label: "Expired", tone: "neutral" },
};

const ALLOWS: Record<string, string> = {
  read_sessions: "View sessions",
  read_settings: "View settings",
  write: "Make changes",
};

export function SupportAccessPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const auth = useAuth();
  const org = auth.me?.orgs.find((o) => o.id === orgId);
  const grants = useQuery({ queryKey: staffKeys.grants(orgId), queryFn: () => supportAccessApi.list(orgId) });
  const [pending, setPending] = useState<PendingAct | null>(null);
  const act = useMutation({
    mutationFn: (args: PendingAct) => {
      if (args.op === "accept") return supportAccessApi.accept(orgId, args.id, args.unmask);
      if (args.op === "deny") return supportAccessApi.deny(orgId, args.id);
      return supportAccessApi.revoke(orgId, args.id);
    },
    onSuccess: (_, args) => {
      setPending(null);
      void qc.invalidateQueries({ queryKey: staffKeys.grants(orgId) });
      toast.push(
        "success",
        args.op === "accept" ? "Support access accepted." : args.op === "deny" ? "Support access denied." : "Support access revoked.",
      );
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const ask = (next: PendingAct) => {
    if (next.op === "accept" && !next.unmask) {
      act.mutate(next);
      return;
    }
    setPending(next);
  };

  const copy: Record<PendingAct["op"], { title: string; confirm: string; body: string }> = {
    accept: {
      title: "Accept support access",
      confirm: "Accept access",
      body: `${pending?.who} can see unmasked content for ${pending?.length || "the time they asked"}.`,
    },
    deny: {
      title: "Deny support access",
      confirm: "Deny request",
      body: `${pending?.who} will not get access.`,
    },
    revoke: {
      title: "Revoke support access",
      confirm: "Revoke access",
      body: `${pending?.who} loses access immediately.`,
    },
  };
  const dialog = pending ? copy[pending.op] : copy.deny;
  const items = (grants.data?.items ?? []).filter((g) => g.status !== "denied" && g.status !== "revoked");

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Support access" subtitle="Backstory asks before anyone on the platform team can open this workspace." />
      {grants.isPending ? (
        <div className="support-grants" aria-hidden="true">
          <div className="card support-grant"><span className="skeleton" style={{ width: "40%", height: 14 }} /><span className="skeleton" style={{ width: "70%", height: 14 }} /></div>
          <div className="card support-grant"><span className="skeleton" style={{ width: "30%", height: 14 }} /><span className="skeleton" style={{ width: "55%", height: 14 }} /></div>
        </div>
      ) : grants.isError ? (
        <ErrorState error={grants.error} retry={() => void grants.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState mark={<EmptyListMark />} title="No support requests" hint="A request shows up here when Backstory asks to look at this workspace." />
      ) : (
        <div className="support-grants">
          {items.map((g) => (
            <GrantCard key={g.id} grant={g} phi={!!org?.settings.phi} busy={act.isPending} onAsk={ask} />
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!pending}
        danger={pending?.op !== "accept"}
        title={dialog.title}
        confirmLabel={dialog.confirm}
        busy={act.isPending}
        onClose={() => { if (!act.isPending) setPending(null); }}
        onConfirm={() => pending && act.mutate(pending)}
      >
        <p style={{ margin: 0 }}>{dialog.body}</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}

function GrantCard({ grant: g, phi, busy, onAsk }: { grant: SupportAccessGrant; phi: boolean; busy: boolean; onAsk: (next: PendingAct) => void }) {
  const who = g.requesterEmail || "Backstory";
  const status = STATUS[g.status] ?? { label: g.status, tone: "neutral" };
  const allows = g.scopes.map((s) => ALLOWS[s]).filter(Boolean);
  const unmask = !!(phi && g.scopes.includes("unmask"));
  const length = formatAccessLength(g.createdAt, g.expiresAt);
  const when = g.status === "pending"
    ? (length ? `For ${length}` : `Until ${formatDateTime(g.expiresAt)}`)
    : g.status === "active"
      ? `Until ${formatDateTime(g.expiresAt)}`
      : g.decidedAt ? formatDateTime(g.decidedAt) : formatDateTime(g.expiresAt);
  return (
    <article className="card support-grant">
      <div className="support-grant-top">
        <span className={`badge ${status.tone}`}>{status.label}</span>
        <span className="muted small">{when}</span>
      </div>
      <div className="support-grant-who">{who}</div>
      <p>{g.reason}</p>
      {allows.length > 0 && (
        <div className="pill-row">
          {allows.map((label) => (
            <span key={label} className="badge neutral">{label}</span>
          ))}
        </div>
      )}
      {g.status === "pending" && (
        <div className="support-grant-actions">
          <button className="btn primary" type="button" disabled={busy} onClick={() => onAsk({ id: g.id, op: "accept", who, unmask, length })}>
            Accept
          </button>
          <button className="btn" type="button" disabled={busy} onClick={() => onAsk({ id: g.id, op: "deny", who, unmask: false, length })}>
            Deny
          </button>
        </div>
      )}
      {g.status === "active" && (
        <div className="support-grant-actions">
          <button className="btn" type="button" disabled={busy} onClick={() => onAsk({ id: g.id, op: "revoke", who, unmask: false, length })}>
            End access
          </button>
        </div>
      )}
    </article>
  );
}
